'use client';

import { signInWithMagicLinkAction } from '@/actions/auth/sign-in-action';
import {
  type SignInWithMagicLinkInput,
  signInWithMagicLinkSchema,
} from '@/schemas/sign-in-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@vibe/design-system/ui/accordion';
import { Button } from '@vibe/design-system/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@vibe/design-system/ui/form';
import { Input } from '@vibe/design-system/ui/input';
import { Loader, Wand } from 'lucide-react';
import { useAction } from 'next-safe-action/hooks';
import { useForm } from 'react-hook-form';

export function SignInAlternatives() {
  const {
    execute,
    isPending,
    result: { data },
  } = useAction(signInWithMagicLinkAction);

  const form = useForm<SignInWithMagicLinkInput>({
    resolver: zodResolver(signInWithMagicLinkSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: SignInWithMagicLinkInput) => {
    execute(data);
  };

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1" className="border-0">
        <AccordionTrigger className="flex justify-center space-x-2 text-sm">
          <span>Esqueceu a senha?</span>
        </AccordionTrigger>
        <AccordionContent className="mt-4">
          <div className="flex flex-col space-y-6">
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-semibold">Acesse apenas com o e-mail</h3>
                  <p className="text-muted-foreground text-sm">
                    Enviaremos um link para o seu e-mail, permitindo que você
                    acesse sua conta sem a necessidade de digitar a senha.
                  </p>
                </div>

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                  >
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="exemplo@email.com"
                              type="email"
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full py-6"
                      disabled={isPending || !form.formState.isValid}
                    >
                      {isPending ? (
                        <div className="flex items-center gap-2">
                          <Loader className="h-4 w-4 animate-spin" />
                          <span>Aguarde...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Wand className="h-4 w-4" />
                          <span>Enviar link de acesso</span>
                        </div>
                      )}
                    </Button>

                    {data?.error && (
                      <p className="text-center text-destructive text-sm">
                        {data.error}
                      </p>
                    )}
                    {data?.data && (
                      <p className="text-center text-sm text-success">
                        {data.data}
                      </p>
                    )}
                  </form>
                </Form>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
